type DeploymentKind = 'development' | 'preview' | 'production' | 'test';

export interface AnalyticsConfiguration {
  readonly enabled: boolean;
  readonly deployment: DeploymentKind;
  readonly measurementId?: string;
}

export interface AnalyticsBrowser {
  readonly location: Pick<Location, 'hash' | 'origin' | 'pathname' | 'search'>;
  readonly appendScript: (source: string) => Promise<void>;
  readonly send: (command: 'config' | 'event' | 'js', ...values: readonly unknown[]) => void;
}

export type AnalyticsStartResult =
  | { readonly status: 'disabled' }
  | { readonly status: 'rejected' }
  | { readonly status: 'started'; readonly pageLocation: string };

const measurementIdPattern = /^G-[A-Z0-9]{6,16}$/u;
const analyticsScriptOrigin = 'https://www.googletagmanager.com';

export const productionAnalyticsConfiguration: AnalyticsConfiguration = Object.freeze({
  enabled: false,
  deployment: 'production',
});

const analyticsPageAllowlist = Object.freeze({
  origins: Object.freeze(['https://map.mountainbikezug.ch']),
  pathnames: Object.freeze(['/', '/index.html', '/index-maplibre.html']),
});

export function allowlistedAnalyticsPageLocation(
  location: Pick<Location, 'origin' | 'pathname'>,
  allowlist = analyticsPageAllowlist,
): string | undefined {
  if (!allowlist.origins.includes(location.origin)) return undefined;
  if (!allowlist.pathnames.includes(location.pathname)) return undefined;
  return `${location.origin}${location.pathname}`;
}

export function createBrowserAnalytics(browserWindow: Window): AnalyticsBrowser {
  let dataLayer: unknown[][] | undefined;
  return Object.freeze({
    location: browserWindow.location,
    appendScript(source: string): Promise<void> {
      return new Promise((resolve, reject) => {
        const script = browserWindow.document.createElement('script');
        script.async = true;
        script.src = source;
        script.onload = () => {
          resolve();
        };
        script.onerror = () => {
          reject(new Error('analytics unavailable'));
        };
        browserWindow.document.head.append(script);
      });
    },
    send(command: 'config' | 'event' | 'js', ...values: readonly unknown[]): void {
      dataLayer ??= [];
      Reflect.set(browserWindow, 'dataLayer', dataLayer);
      dataLayer.push([command, ...values]);
    },
  });
}

export function startAnalytics(
  browser: AnalyticsBrowser,
  configuration: AnalyticsConfiguration,
): AnalyticsStartResult {
  if (!configuration.enabled || configuration.deployment !== 'production') {
    return { status: 'disabled' };
  }
  const measurementId = configuration.measurementId;
  const pageLocation = allowlistedAnalyticsPageLocation(browser.location);
  if (
    measurementId === undefined ||
    !measurementIdPattern.test(measurementId) ||
    pageLocation === undefined
  ) {
    return { status: 'rejected' };
  }

  browser.send('js', new Date());
  browser.send('config', measurementId, { send_page_view: false });
  browser.send('event', 'page_view', {
    page_location: pageLocation,
    page_path: browser.location.pathname,
  });
  const scriptUrl = new URL('/gtag/js', analyticsScriptOrigin);
  scriptUrl.searchParams.set('id', measurementId);
  void browser.appendScript(scriptUrl.href).catch(() => undefined);
  return { status: 'started', pageLocation };
}
